/**
 * Builds a `page_view` event from a router state.
 *
 * ## The query string is never serialised wholesale
 *
 * Our URLs carry secrets in query params, not path segments:
 *
 *   - `?token=` — invitations, email verification, **password reset**
 *   - `?email=` / `?email_address=` — PII on signup and invitation accept
 *   - `?redirect_url=` — an open-redirect surface, and a URL that can carry
 *     anything in its own query string
 *
 * So this is an **allowlist**, not a denylist. A denylist fails open: the next
 * person to add `?magic_link=` leaks it silently into a table that is retained
 * indefinitely, and remediating that means rewriting history. Anything not named
 * in ALLOWED_QUERY_PARAMS never leaves the browser.
 *
 * If you need a new dimension, add the param name below and think for a second
 * about whether it can ever hold a credential. The cost of getting this wrong is
 * not a missing chart.
 *
 * The referrer gets the same treatment for the same reason: an internal
 * navigation away from `/accept/reset-password?token=…` would otherwise put that
 * token in `document.referrer` and straight into the payload.
 */

import { getSessionId } from "./session";
import { resolveRoutePattern } from "./routePattern";
import type { PageViewEvent } from "./types";

/**
 * Query params safe to record. Deliberately tiny.
 *
 * NEVER add: token, email, email_address, redirect_url, or anything whose value
 * a user could not see printed in an analytics dashboard.
 */
const ALLOWED_QUERY_PARAMS = ["type"] as const;

interface RouterStateLike {
  location: { pathname: string; search?: string };
  matches?: Array<{ route?: { path?: string }; params?: Record<string, string | undefined> }>;
}

const allowlistedQuery = (search: string): Record<string, string> => {
  if (!search) return {};

  const params = new URLSearchParams(search);

  return ALLOWED_QUERY_PARAMS.reduce<Record<string, string>>((acc, key) => {
    const value = params.get(key);
    if (value !== null) acc[key] = value;
    return acc;
  }, {});
};

/**
 * Same-origin referrers keep their pathname (useful — it is the previous page)
 * but lose the query string, which is where our tokens live. Cross-origin
 * referrers are reduced to their origin: knowing traffic came from LinkedIn is
 * the useful part, and a third party's query string is not ours to store.
 */
const sanitizeReferrer = (referrer: string): string | null => {
  if (!referrer) return null;

  try {
    const url = new URL(referrer);
    return url.origin === window.location.origin ? url.pathname : url.origin;
  } catch {
    return null;
  }
};

const toId = (value: string | undefined): number | null => {
  if (!value) return null;
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

export const buildPageViewEvent = (state: RouterStateLike): PageViewEvent => {
  const { pathname, search = "" } = state.location;
  const { route, params } = resolveRoutePattern(state.matches ?? [], pathname);

  return {
    name: "page_view",
    occurred_at: new Date().toISOString(),
    session_id: getSessionId(),
    // Read off the route rather than app state, so a page view is attributable
    // without waiting for a query to resolve. The server treats these as
    // spoofable hints.
    hub_id: toId(params.h_id),
    project_id: toId(params.p_id),
    properties: {
      route,
      // Pathname only. `search` is intentionally not here.
      path: pathname,
      params,
      query: allowlistedQuery(search),
      referrer: sanitizeReferrer(typeof document === "undefined" ? "" : document.referrer),
      viewport: { w: window.innerWidth, h: window.innerHeight },
    },
  };
};
